// ── BCHNostr — Podcast promotion ──────────────────────────────────
//
// Reuses the app's existing boost/promotion machinery rather than
// introducing an advertising engine:
//
//   · the same competitive bid auction (name your price, highest wins),
//   · the same 6-hour window,
//   · the same platform wallet,
//   · the same trust gate that governs post boosts,
//   · the same publish-then-pay ordering, so a denied signature or a
//     failed relay never costs the user any BCH.
//
// Only the target differs: a podcast SHOW page, or a single EPISODE.

import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Rocket, X, Check, Wallet, BadgeCheck, TrendingUp, Clock, Mic2 } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useTrustScore } from '../hooks/useTrustScore';
import { canPurchaseBoost } from '../lib/trustScore';
import { usdToSats, satsToUsdStr } from '../lib/boost';
import {
  PODCAST_BOOST_KIND, PODCAST_BOOST_TAG, PODCAST_BOOST_WALLET,
  PODCAST_BOOST_DURATION, PODCAST_BOOST_MIN_BID_USD, PODCAST_BOOST_BID_PRESETS,
  PODCAST_BOOST_MAX_VISIBLE, podcastBoostDTag, activePodcastBoosts, episodeSlug,
  type PodcastBoostData,
} from '../lib/podcast';

interface PodcastBoostModalProps {
  podcastPubkey: string;
  podcastName: string;
  /** Present when promoting a single episode rather than the whole show. */
  episodeGuid?: string;
  episodeTitle?: string;
  onClose: () => void;
}

export default function PodcastBoostModal({
  podcastPubkey, podcastName, episodeGuid, episodeTitle, onClose,
}: PodcastBoostModalProps) {
  const { user, setShowLoginDialog } = useAppContext();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const trust = useTrustScore(user?.pubkey);
  // Podcast promotion inherits the boost eligibility rules verbatim —
  // promotion abuse is the same problem regardless of what's promoted.
  const eligibility = canPurchaseBoost(trust);

  const [bidUsd, setBidUsd] = useState(1);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [walletInitializing, setWalletInitializing] = useState(false);

  const target: 'show' | 'episode' = episodeGuid ? 'episode' : 'show';
  const bidSats = usdToSats(bidUsd, bchPrice);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = bidSats !== null && walletBalance >= bidSats;
  const bidTooLow = bidUsd < PODCAST_BOOST_MIN_BID_USD;

  // What we're bidding against.
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 2 * 86400);
  const { events: boostEvents } = useNostrQuery({
    filters: [{ kinds: [PODCAST_BOOST_KIND], '#t': [PODCAST_BOOST_TAG], since, limit: 100 }],
    allRelays: true,
  });

  const leaderboard = useMemo(
    () => activePodcastBoosts(boostEvents).filter(b => b.target === target).slice(0, 10),
    [boostEvents, target],
  );

  const projectedRank = useMemo(() => {
    if (bidSats === null) return leaderboard.length + 1;
    const pos = leaderboard.findIndex(b => bidSats > b.priceSats);
    return pos >= 0 ? pos + 1 : leaderboard.length + 1;
  }, [bidSats, leaderboard]);

  /** An existing active promotion for exactly this target, if any. */
  const existing = useMemo(() => {
    if (!user) return null;
    return activePodcastBoosts(boostEvents).find(b =>
      b.boostedBy === user.pubkey &&
      b.podcastPubkey === podcastPubkey &&
      (target === 'show'
        ? b.target === 'show'
        : b.target === 'episode' && episodeSlug(b.episodeGuid) === episodeSlug(episodeGuid || '')),
    ) || null;
  }, [boostEvents, user, podcastPubkey, target, episodeGuid]);

  const handleCustomInput = (val: string) => {
    setCustomInput(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) setBidUsd(Math.round(num * 100) / 100);
  };

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handleBoost = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (!canAfford || bidSats === null || bidTooLow || !eligibility.eligible) return;

    setBoosting(true);
    setError(null);

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = now + PODCAST_BOOST_DURATION;
    const data: PodcastBoostData = {
      target,
      podcastPubkey,
      episodeGuid: episodeGuid || '',
      priceSats: bidSats,
      expiresAt,
      boostedBy: user.pubkey,
      bidUsd,
    };
    const dTag = podcastBoostDTag(podcastPubkey, episodeGuid);
    const baseTags: string[][] = [
      ['d', dTag],
      ['t', PODCAST_BOOST_TAG],
      ['t', 'bch'],
      ['p', podcastPubkey],
      ['expires', String(expiresAt)],
      ['amount', String(bidSats)],
      ['alt', `Promoting the podcast "${podcastName}" on BCHNostr`],
    ];

    // ── Step 1: publish FIRST, pay second ──
    // If signing is denied or every relay rejects the event, the user has
    // not spent a single satoshi.
    try {
      await publish({
        kind: PODCAST_BOOST_KIND,
        content: JSON.stringify(data),
        tags: baseTags,
        created_at: now,
      });
    } catch (e) {
      setBoosting(false);
      setError(
        e instanceof Error && (e.message.includes('denied') || e.message.includes('reject'))
          ? 'Signing was denied. No BCH was charged.'
          : 'Couldn’t publish the promotion. No BCH was charged — please try again.',
      );
      return;
    }

    // ── Step 2: pay ──
    try {
      const txid = await tipWallet.sendBCH(PODCAST_BOOST_WALLET, BigInt(bidSats));
      // Re-publish with the txid so the promotion is verifiable on-chain.
      // Best-effort: the promotion is already live either way.
      publish({
        kind: PODCAST_BOOST_KIND,
        content: JSON.stringify({ ...data, txid }),
        tags: [...baseTags, ['txid', txid]],
        created_at: now + 1,
      }).catch(() => {});
      setSuccess(true);
      tipWallet.refreshBalance();
    } catch (e) {
      // The event landed but payment didn't — say so plainly rather than
      // pretending either outcome.
      setSuccess(true);
      setError('Your promotion is live, but the payment may not have completed. Check your wallet balance.');
    } finally {
      setBoosting(false);
    }
  };

  const label = target === 'episode' ? 'Boost Episode' : 'Promote Podcast';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="font-bold text-base flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            {label}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pb-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* What's being promoted */}
          <div className="flex items-start gap-2 rounded-xl bg-muted/50 border border-border p-2.5">
            <Mic2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{podcastName}</p>
              {episodeTitle && (
                <p className="text-[11px] text-muted-foreground line-clamp-2">{episodeTitle}</p>
              )}
            </div>
          </div>

          {trust.loading ? (
            <div className="text-center py-10 space-y-3">
              <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
              <p className="text-xs text-muted-foreground">Checking eligibility…</p>
            </div>
          ) : !eligibility.eligible ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <BadgeCheck className="w-7 h-7 text-primary" />
              </div>
              <p className="text-sm font-bold">Verification Required</p>
              <p className="text-xs text-muted-foreground leading-relaxed px-2">{eligibility.reason}</p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-muted/80">
                Got it
              </button>
            </div>
          ) : existing && !success ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
                <Rocket className="w-7 h-7 text-amber-500" />
              </div>
              <p className="text-sm font-bold">Already promoted</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your promotion expires in{' '}
                <span className="font-semibold text-foreground">
                  {(() => {
                    const left = Math.max(0, existing.expiresAt - Math.floor(Date.now() / 1000));
                    if (left <= 0) return 'a moment';
                    const h = Math.floor(left / 3600);
                    const m = Math.floor((left % 3600) / 60);
                    return h > 0 ? `${h}h ${m}m` : `${m}m`;
                  })()}
                </span>
                . You can promote it again once it expires.
              </p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-muted/80">
                Got it
              </button>
            </div>
          ) : success ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-emerald-500" />
              </div>
              <p className="text-sm font-bold text-emerald-500">
                {target === 'episode' ? 'Episode boosted!' : 'Podcast promoted!'}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                It will be featured in BCH Podcasts for the next 6 hours.
              </p>
              {error && <p className="text-[11px] text-amber-600 dark:text-amber-400">{error}</p>}
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Name your price. Higher bids get featured higher in BCH Podcasts.
                All promotions last <strong className="text-foreground">6 hours</strong>.
              </p>

              {/* Bid */}
              <div className="space-y-3">
                <label className="text-xs font-semibold flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-primary" /> Your Bid
                </label>
                <div className="flex gap-1.5 flex-wrap">
                  {PODCAST_BOOST_BID_PRESETS.map(p => (
                    <button
                      key={p.usd}
                      onClick={() => { setBidUsd(p.usd); setShowCustom(false); setCustomInput(''); }}
                      className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                        bidUsd === p.usd && !showCustom
                          ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                          : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <span>{p.emoji}</span><span>{p.label}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCustom(v => !v)}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                      showCustom
                        ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                        : 'border-border hover:border-primary/40 text-muted-foreground'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {showCustom && (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">$</span>
                    <input
                      type="number"
                      min={PODCAST_BOOST_MIN_BID_USD}
                      step="0.05"
                      value={customInput}
                      onChange={e => handleCustomInput(e.target.value)}
                      placeholder={`Min $${PODCAST_BOOST_MIN_BID_USD.toFixed(2)}`}
                      className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-border bg-muted/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
                      autoFocus
                    />
                  </div>
                )}

                <div className="rounded-xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Your bid</span>
                    <div className="text-right">
                      <span className="text-sm font-bold">${bidUsd.toFixed(2)}</span>
                      <span className="text-[10px] text-muted-foreground font-mono ml-1.5">
                        {bidSats ? `~${bidSats.toLocaleString()} sats` : '…'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Duration</span>
                    <span className="text-xs font-semibold">6 hours</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Projected rank</span>
                    <span className={`text-xs font-bold ${
                      projectedRank === 1 ? 'text-amber-500'
                        : projectedRank <= PODCAST_BOOST_MAX_VISIBLE ? 'text-primary'
                        : 'text-muted-foreground'
                    }`}>
                      #{projectedRank}
                      {projectedRank > PODCAST_BOOST_MAX_VISIBLE && (
                        <span className="text-muted-foreground font-normal ml-1">
                          · top {PODCAST_BOOST_MAX_VISIBLE} are featured
                        </span>
                      )}
                    </span>
                  </div>
                  {bidTooLow && (
                    <p className="text-[10px] text-red-500 font-medium">
                      Minimum bid is ${PODCAST_BOOST_MIN_BID_USD.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Current bids */}
              {leaderboard.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <p className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
                    Current bids ({leaderboard.length})
                  </p>
                  {leaderboard.slice(0, 5).map((b, i) => {
                    const left = Math.max(0, b.expiresAt - Math.floor(Date.now() / 1000));
                    const h = Math.floor(left / 3600);
                    const m = Math.floor((left % 3600) / 60);
                    return (
                      <div
                        key={`${b.podcastPubkey}:${b.episodeGuid}:${i}`}
                        className="flex items-center justify-between px-3 py-2 border-t border-border"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-extrabold w-5 text-center ${
                            i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : 'text-muted-foreground'
                          }`}>
                            #{i + 1}
                          </span>
                          <span className="text-xs font-bold">
                            {satsToUsdStr(b.priceSats, bchPrice) || `${b.priceSats.toLocaleString()} sats`}
                          </span>
                        </div>
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Clock className="w-2.5 h-2.5" />{h > 0 ? `${h}h ${m}m` : `${m}m`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Wallet */}
              {!tipWallet.ready ? (
                <div className="rounded-xl bg-muted/50 border border-border p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-2">Initialize your wallet to promote</p>
                  <button
                    onClick={handleInitWallet}
                    disabled={walletInitializing}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 mx-auto"
                  >
                    {walletInitializing
                      ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <Wallet className="w-3.5 h-3.5" />}
                    {walletInitializing ? 'Loading…' : 'Load Wallet'}
                  </button>
                </div>
              ) : (
                <div className="rounded-xl bg-muted/50 border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Wallet Balance</span>
                    <span className={`text-xs font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                      {satsToUsdStr(walletBalance, bchPrice) || `${walletBalance.toLocaleString()} sats`}
                    </span>
                  </div>
                  {!canAfford && bidSats !== null && (
                    <p className="text-[10px] text-red-500 mt-1">
                      Not enough funds. Need{' '}
                      {satsToUsdStr(bidSats - walletBalance, bchPrice) || `${(bidSats - walletBalance).toLocaleString()} sats`} more.
                      Deposit to your tip wallet in Settings.
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={handleBoost}
                disabled={!tipWallet.ready || !canAfford || boosting || bidTooLow}
                className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  tipWallet.ready && canAfford && !bidTooLow
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:opacity-90 active:scale-[0.98]'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {boosting
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <Rocket className="w-4 h-4" />}
                {boosting ? 'Promoting…' : `Promote for $${bidUsd.toFixed(2)}`}
              </button>

              {error && <p className="text-xs text-red-500 text-center">{error}</p>}

              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                Uses the same bidding system as post boosts. Your bid competes with other
                active podcast promotions.
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
